import { usePropertyStructureType } from '../../../../queries/PropertyFields/TopLevelFields';
import {
    usePropertyNumberUnits,
    usePropertyUnitsIncomeRestricted,
    usePropertyVariousUnitsDescription,
} from '../../../../queries/PropertyFields/NonRentalCoreFields';
import * as Store from '@apply/store';
import { StructureType } from '../StructureType';
import { ShowIf } from '../../../components/ShowIf';
import { UnitsDropDown } from './components/UnitsDropDown';
import { IncomeRestrictedUnitsDropDown } from './components/IncomeRestrictedUnitsDropDown';
import { VariousUnitsDescription } from './components/VariousUnitsDescription';

const VARIOUS_MIN_UNITS = 1;
const VARIOUS_INCOME_RESTRICTED_THRESHOLD = 5;

interface StructureAndUnitsProps {
    propertyGuid: string;
    isReview?: boolean;
}
export const StructureAndUnits: React.FC<StructureAndUnitsProps> = ({ propertyGuid, isReview = false }) => {
    const [structureType] = usePropertyStructureType(propertyGuid);
    const [numberUnits, set_numberUnits] = usePropertyNumberUnits(propertyGuid);
    const [unitsIncomeRestricted, set_unitsIncomeRestricted] = usePropertyUnitsIncomeRestricted(propertyGuid);
    const [, set_variousUnitsDescription] = usePropertyVariousUnitsDescription(propertyGuid);

    const isVarious = structureType === Store.PropertiesStore.Types.variousStructureType;
    const showVariousIncomeRestricted = (numberUnits ?? 0) >= VARIOUS_INCOME_RESTRICTED_THRESHOLD;

    const handleStructureTypeChange = (newValue: string) => {
        const diffValue = newValue != structureType;
        if (diffValue) {
            set_unitsIncomeRestricted(undefined);
            set_variousUnitsDescription(undefined);
            // StructureType only presets the unit count for the fixed-size types, so Various starts blank.
            if (newValue === Store.PropertiesStore.Types.variousStructureType) {
                set_numberUnits(undefined);
            }
        }
    };

    // The income-restricted answer is only valid while it is still asked and fits under the chosen unit count.
    const handleVariousUnitsChange = (newValue: number | undefined) => {
        const belowThreshold = (newValue ?? 0) < VARIOUS_INCOME_RESTRICTED_THRESHOLD;
        const exceedsUnits = unitsIncomeRestricted != undefined && newValue != undefined && unitsIncomeRestricted > newValue;
        if (belowThreshold || exceedsUnits) {
            set_unitsIncomeRestricted(undefined);
        }
    };

    return (
        <>
            <StructureType
                propertyGuid={propertyGuid}
                isReview={isReview}
                structureTypes={Store.PropertiesStore.Types.fixFlipBridgeStructureTypes}
                onValueChange={(newValue) => handleStructureTypeChange(newValue)}
            />
            <ShowIf showIf={structureType === '5+ Unit'}>
                <UnitsDropDown
                    propertyGuid={propertyGuid}
                    isReview={isReview}
                />
                <ShowIf showIf={numberUnits != undefined && numberUnits > 0}>
                    <IncomeRestrictedUnitsDropDown
                        propertyGuid={propertyGuid}
                        isReview={isReview}
                        numberOfUnits={numberUnits}
                    />
                </ShowIf>
            </ShowIf>
            <ShowIf showIf={isVarious}>
                <UnitsDropDown
                    propertyGuid={propertyGuid}
                    isReview={isReview}
                    minimum={VARIOUS_MIN_UNITS}
                    onValueChange={handleVariousUnitsChange}
                />
                <ShowIf showIf={showVariousIncomeRestricted}>
                    <IncomeRestrictedUnitsDropDown
                        propertyGuid={propertyGuid}
                        isReview={isReview}
                        numberOfUnits={numberUnits}
                    />
                </ShowIf>
                <VariousUnitsDescription
                    propertyGuid={propertyGuid}
                    isReview={isReview}
                />
            </ShowIf>
        </>
    );
};
